import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { getRooms, updateRoom, updateOccupancy } from "../lib/api";
import { toast } from "sonner";
import { Users, Euro, TrendingUp, TrendingDown, Edit2, Bed } from "lucide-react";

const RoomCard = ({ room, onUpdate, onOccupancyChange }) => {
  const [editing, setEditing] = useState(false);
  const [basePrice, setBasePrice] = useState(room.base_price);
  const [occupancy, setOccupancy] = useState(room.occupied_rooms);
  
  const occupancyRate = room.total_rooms > 0 
    ? ((room.occupied_rooms / room.total_rooms) * 100).toFixed(1) 
    : 0;
  
  const priceChange = room.current_price - room.base_price;
  const priceChangePercent = room.base_price > 0 
    ? ((priceChange / room.base_price) * 100).toFixed(1) 
    : 0;

  const handleSavePrice = async () => {
    try {
      await onUpdate(room.id, { base_price: parseFloat(basePrice) });
      setEditing(false);
      toast.success("Precio base actualizado");
    } catch (error) {
      toast.error("Error al actualizar precio");
    }
  };

  const handleOccupancyChange = async (value) => {
    const newOccupancy = value[0];
    setOccupancy(newOccupancy);
    try {
      await onOccupancyChange(room.id, newOccupancy);
    } catch (error) {
      toast.error("Error al actualizar ocupación");
    }
  };

  return (
    <Card className="room-card overflow-hidden" data-testid={`room-card-${room.id}`}>
      <div className="relative">
        <img
          src={room.image_url || "https://images.pexels.com/photos/3755556/pexels-photo-3755556.jpeg"}
          alt={room.name}
          className="room-card-image"
        />
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            occupancyRate >= 80 ? "bg-red-500 text-white" :
            occupancyRate >= 50 ? "bg-amber-500 text-white" :
            "bg-emerald-500 text-white"
          }`}>
            {occupancyRate}% Ocupación
          </span>
        </div>
      </div>
      
      <CardContent className="p-4 space-y-4">
        <div>
          <h3 className="font-semibold text-lg">{room.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{room.description}</p>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{room.capacity} personas</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Bed className="w-4 h-4" />
            <span>{room.total_rooms} habitaciones</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground">Precio Base</p>
            <p className="text-lg font-bold">€{room.base_price}</p>
          </div>
          <div className="p-3 bg-primary/5 rounded-lg">
            <p className="text-xs text-muted-foreground">Precio Actual</p>
            <div className="flex items-center gap-2">
              <p className="text-lg font-bold">€{room.current_price}</p>
              {priceChange !== 0 && (
                <span className={`flex items-center text-xs ${priceChange > 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {priceChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {priceChangePercent}%
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Ocupación</span>
            <span className="font-medium">{occupancy} / {room.total_rooms}</span>
          </div>
          <Slider
            value={[occupancy]}
            onValueChange={handleOccupancyChange}
            max={room.total_rooms}
            step={1}
            className="w-full"
            data-testid={`occupancy-slider-${room.id}`}
          />
        </div>

        <Dialog open={editing} onOpenChange={setEditing}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full" data-testid={`edit-room-btn-${room.id}`}>
              <Edit2 className="w-4 h-4 mr-2" />
              Editar Precio Base
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar {room.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="base-price">Precio Base (€)</Label>
                <Input
                  id="base-price"
                  type="number"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  data-testid="base-price-input"
                />
              </div>
              <Button onClick={handleSavePrice} className="w-full" data-testid="save-price-btn">
                Guardar Cambios
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const Rooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRooms = async () => {
    try {
      const response = await getRooms();
      setRooms(response.data);
    } catch (error) {
      console.error("Error fetching rooms:", error);
      toast.error("Error al cargar habitaciones");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRoom = async (id, data) => {
    await updateRoom(id, data);
    fetchRooms();
  };

  const handleOccupancyChange = async (id, occupiedRooms) => {
    await updateOccupancy(id, { room_type_id: id, occupied_rooms: occupiedRooms });
    fetchRooms();
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="rooms-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Habitaciones</h1>
        <p className="text-muted-foreground">Gestiona los tipos de habitaciones y sus precios</p>
      </div>

      {rooms.length === 0 ? (
        <Card className="p-12 text-center">
          <Bed className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No hay habitaciones</h3>
          <p className="text-muted-foreground mb-4">
            Ve al Dashboard y haz clic en "Cargar Datos" para añadir habitaciones de ejemplo.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rooms.map((room) => (
            <RoomCard
              key={room.id}
              room={room}
              onUpdate={handleUpdateRoom}
              onOccupancyChange={handleOccupancyChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Rooms;
